import React, { type FC, useEffect, useRef, useState } from 'react';

import { logEvent } from '../../lib/analytics';
import type { VoiceDemoDataType } from '../../lib/types';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { ChevronDown } from '../icons/ChevronDown';
import {
  countLanguages,
  getCountryNameFromLanguageCode,
  useIsMobileDevice,
} from '../../lib/utils';

const FlagIcon = ({ languageCode }: { languageCode: string }) => {
  const countryCode = languageCode.split('-')[1] || languageCode;
  const flag = countryCode
    .toUpperCase()
    .split('')
    .map((char) => String.fromCodePoint(0x1f1e6 - 65 + char.charCodeAt(0)))
    .join('');

  return (
    <div className="text-[1rem]">
      <span className="-p-2 h-1">{flag}</span>
    </div>
  );
};

const VoiceOption: FC<
  VoiceDemoDataType & {
    handleUpdateLanguage: (languageCode: string) => void;
    showCountryName: boolean;
  }
> = (language) => {
  return (
    <button
      className="flex items-center gap-2 px-4 py-3 text-xs font-medium hover:bg-[#F5F5FA]"
      data-active={true}
      onClick={() => language.handleUpdateLanguage(language.languageCode)}>
      <FlagIcon languageCode={language.languageCode} />
      <div className="min-w-0 whitespace-nowrap text-[1rem]">
        {language.languageName}{' '}
        {language.showCountryName
          ? '(' + getCountryNameFromLanguageCode(language.languageCode) + ')'
          : ''}
      </div>
    </button>
  );
};

interface Props {
  allLanguages: VoiceDemoDataType[];
  onChange?: (v: string) => void;
  triggerClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const LanguageSelector: FC<Props> = ({ allLanguages }) => {
  const { currentLanguage, handleUpdateLanguage } = useVoiceDemo();
  const currentLanguageCode = currentLanguage.languageCode;
  const availableLanguages = allLanguages.filter(
    (v) => v.languageCode !== currentLanguageCode,
  );
  const isMobile = useIsMobileDevice();
  const language = allLanguages.find(
    (v) => v.languageCode === currentLanguageCode,
  );
  const languageCount = countLanguages(
    availableLanguages.map((l) => l.languageCode),
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    if (!isDropdownOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const dropdownHeight = 320;
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceAbove >= dropdownHeight || spaceAbove > spaceBelow);
    }
    setIsDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <div
      className="relative flex flex-1 @sm:max-w-[180px] @sm:py-1"
      ref={dropdownRef}>
      {language && (
        <button
          key={language.languageName}
          className="flex flex-1 cursor-pointer items-center gap-2 rounded-2xl bg-[#F5F5FA] px-[16px] py-[12px] leading-5 hover:border-[#e4eaf1] hover:bg-[#e4eaf1] @sm:py-[14px]"
          onClick={() => {
            logEvent('language_drowpdown_clicked', {
              isOpen: isDropdownOpen,
            });
            toggleDropdown();
          }}>
          <FlagIcon languageCode={currentLanguageCode} />

          <div className="text-[1rem]">{language.languageName}</div>
          <div className="ml-auto">
            <ChevronDown />
          </div>
        </button>
      )}
      {isDropdownOpen && (
        <div
          className={`dropdown-shadow absolute ${isMobile ? 'right-0' : 'left-0'} z-50 flex h-[320px] max-w-[100vw] flex-col overflow-scroll rounded border border-[#E9EAF0] bg-white no-scrollbar ${openUpward ? 'bottom-[60px] mb-1' : 'top-[60px] mt-1'}`}>
          {availableLanguages.map((v) => (
            <VoiceOption
              key={v.languageCode}
              {...v}
              showCountryName={languageCount[v.languageCode.split('-')[0]] > 1}
              handleUpdateLanguage={(lang) => {
                logEvent('language_updated', {
                  name: lang,
                });
                setIsDropdownOpen(false);
                handleUpdateLanguage(lang);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
